// Trigger Filter Evaluation Engine
// Evaluates if an event matches the defined trigger filters

import { TriggerFilter, TriggerFilterGroup } from '../types/triggerFilters';
import { EcommerceEvent } from '../types/ecommerceEvents';

// Get value from object using JSON path (e.g., "addedItem.productPrice")
function getValueByPath(obj: any, path: string): any {
  // Handle array notation (e.g., "productCategories[0].id")
  const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
  const keys = normalizedPath.split('.');
  
  let value = obj;
  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    value = value[key];
  }
  
  return value;
}

// Evaluate a single filter against event data
export function evaluateFilter(filter: TriggerFilter, eventData: any): boolean {
  const fieldValue = getValueByPath(eventData, filter.field);
  const { operator, value } = filter;
  
  // Handle existence operators
  if (operator === 'exists') {
    return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
  }
  
  if (operator === 'does_not_exist') {
    return fieldValue === null || fieldValue === undefined || fieldValue === '';
  }
  
  // If field doesn't exist and we're checking other operators, return false
  if (fieldValue === null || fieldValue === undefined) {
    return false;
  }
  
  // Convert to strings for string operations
  const fieldStr = String(fieldValue).toLowerCase();
  const valueStr = value ? String(value).toLowerCase() : '';
  
  // Evaluate based on operator
  switch (operator) {
    case 'is':
      // Exact match (case-insensitive for strings)
      if (typeof fieldValue === 'number' && typeof value === 'number') {
        return fieldValue === value;
      }
      return fieldStr === valueStr;
      
    case 'is_not':
      // Not equal
      if (typeof fieldValue === 'number' && typeof value === 'number') {
        return fieldValue !== value;
      }
      return fieldStr !== valueStr;
      
    case 'contains':
      // Partial match
      return fieldStr.includes(valueStr);
      
    case 'does_not_contain':
      // Negative partial match
      return !fieldStr.includes(valueStr);
      
    case 'starts_with':
      // Prefix match
      return fieldStr.startsWith(valueStr);
      
    case 'ends_with':
      // Suffix match
      return fieldStr.endsWith(valueStr);
      
    case 'greater_than':
      // Numeric comparison
      return Number(fieldValue) > Number(value);
      
    case 'greater_than_or_equal':
      // Numeric comparison
      return Number(fieldValue) >= Number(value);
      
    case 'less_than':
      // Numeric comparison
      return Number(fieldValue) < Number(value);
      
    case 'less_than_or_equal':
      // Numeric comparison
      return Number(fieldValue) <= Number(value);
      
    case 'between':
      // Range check (value should be [min, max])
      if (Array.isArray(value) && value.length === 2) {
        const numValue = Number(fieldValue);
        return numValue >= Number(value[0]) && numValue <= Number(value[1]);
      }
      return false;
      
    case 'in':
      // Check if value is in list
      if (Array.isArray(value)) {
        return value.some(v => String(v).toLowerCase() === fieldStr);
      }
      return false;
      
    case 'not_in':
      // Check if value is not in list
      if (Array.isArray(value)) {
        return !value.some(v => String(v).toLowerCase() === fieldStr);
      }
      return true;
      
    default:
      console.warn(`Unknown filter operator: ${operator}`);
      return false;
  }
}

// Evaluate a filter group with AND/OR logic
export function evaluateFilterGroup(group: TriggerFilterGroup, eventData: any): boolean {
  if (!group.filters || group.filters.length === 0) {
    return true; // Empty group passes
  }
  
  const results = group.filters.map(filterOrGroup => {
    if ('filters' in filterOrGroup) {
      // It's a nested group
      return evaluateFilterGroup(filterOrGroup as TriggerFilterGroup, eventData);
    } else {
      // It's a single filter
      return evaluateFilter(filterOrGroup as TriggerFilter, eventData);
    }
  });
  
  // Apply logic operator
  if (group.logicOperator === 'OR') {
    return results.some(r => r); // At least one must be true
  } else {
    return results.every(r => r); // All must be true (AND)
  }
}

// Evaluate a list of filters (main entry point)
export function evaluateFilters(
  filters: TriggerFilter[],
  eventData: EcommerceEvent | any
): boolean {
  if (!filters || filters.length === 0) {
    return true; // No filters = always pass
  }
  
  // Process filters with their logic operators
  let result = true;
  let currentLogic: 'AND' | 'OR' = 'AND';
  
  for (const filter of filters) {
    const filterResult = evaluateFilter(filter, eventData);
    
    // Apply the logic from the PREVIOUS filter
    if (currentLogic === 'AND') {
      result = result && filterResult;
    } else {
      result = result || filterResult;
    }
    
    // Set logic for next iteration
    currentLogic = filter.logicOperator || 'AND';
  }
  
  return result;
}

// Helper: Test filters against sample data (for UI preview)
export function testFilters(filters: TriggerFilter[], sampleData: any): {
  passes: boolean;
  results: Array<{
    filter: TriggerFilter;
    passed: boolean;
    fieldValue: any;
    reason?: string;
  }>;
} {
  const results = filters.map(filter => {
    const fieldValue = getValueByPath(sampleData, filter.field);
    const passed = evaluateFilter(filter, sampleData);
    
    let reason: string | undefined;
    if (!passed) {
      if (fieldValue === null || fieldValue === undefined) {
        reason = 'Field not found or empty';
      } else {
        reason = `Value "${fieldValue}" does not match condition`;
      }
    }
    
    return {
      filter,
      passed,
      fieldValue,
      reason
    };
  });
  
  const passes = evaluateFilters(filters, sampleData);
  
  return { passes, results };
}

// Helper: Validate filter configuration
export function validateFilter(filter: TriggerFilter): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  if (!filter.field) {
    errors.push('Field is required');
  }
  
  if (!filter.operator) {
    errors.push('Operator is required');
  }
  
  // Check if value is required for this operator
  if (filter.operator && !['exists', 'does_not_exist'].includes(filter.operator)) {
    if (filter.value === null || filter.value === undefined || filter.value === '') {
      errors.push('Value is required for this operator');
    }
    
    // For 'between', need array with 2 values
    if (filter.operator === 'between') {
      if (!Array.isArray(filter.value) || filter.value.length !== 2) {
        errors.push('Between operator requires two values [min, max]');
      }
    }
    
    // For 'in'/'not_in', need array
    if (['in', 'not_in'].includes(filter.operator)) {
      if (!Array.isArray(filter.value) || filter.value.length === 0) {
        errors.push('This operator requires a list of values');
      }
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

// Helper: Get human-readable description of filter
export function describeFilter(filter: TriggerFilter): string {
  const { fieldLabel, operator, value } = filter;
  
  switch (operator) {
    case 'exists':
      return `${fieldLabel} exists`;
    case 'does_not_exist':
      return `${fieldLabel} does not exist`;
    case 'is':
      return `${fieldLabel} is "${value}"`;
    case 'is_not':
      return `${fieldLabel} is not "${value}"`;
    case 'contains':
      return `${fieldLabel} contains "${value}"`;
    case 'does_not_contain':
      return `${fieldLabel} does not contain "${value}"`;
    case 'starts_with':
      return `${fieldLabel} starts with "${value}"`;
    case 'ends_with':
      return `${fieldLabel} ends with "${value}"`;
    case 'greater_than':
      return `${fieldLabel} > ${value}`;
    case 'greater_than_or_equal':
      return `${fieldLabel} >= ${value}`;
    case 'less_than':
      return `${fieldLabel} < ${value}`;
    case 'less_than_or_equal':
      return `${fieldLabel} <= ${value}`;
    case 'between':
      if (Array.isArray(value)) {
        return `${fieldLabel} between ${value[0]} and ${value[1]}`;
      }
      return `${fieldLabel} between values`;
    case 'in':
      if (Array.isArray(value)) {
        return `${fieldLabel} in [${value.join(', ')}]`;
      }
      return `${fieldLabel} in list`;
    case 'not_in':
      if (Array.isArray(value)) {
        return `${fieldLabel} not in [${value.join(', ')}]`;
      }
      return `${fieldLabel} not in list`;
    default:
      return `${fieldLabel} ${operator} ${value}`;
  }
}


